import { useLocation, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";

import { ImprovedParticipantShop } from "../../Interfaces/FundraiserRole";
import { CartItem } from "../../Interfaces/Types";
import {
  createEmptyCartItemNoCustom,
  createEmptyCartItemWithCustom,
} from "../../Interfaces/Types";
import { ImprovedProduct } from "../../Interfaces/Product";
import {
  Category,
  getCategoryRequirements,
  Requirement,
} from "../../CommonFunctions";
import {
  CUSTOMIZATION_OPTIONS,
  AVAILABLE_FONTS,
  DEFAULT_PARTICIPANT,
} from "./constants";
import {
  ProductImage,
  ProductOptions,
  ProductPrice,
  ProductActions,
} from "../../components/SingleProductPage/ProductDetail";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  isBottleCategory,
  isClothingCategory,
  getStyleNameByCategory,
} from "./SingleProductPageHelpers";
import ScrollToTop from "../../layout/ScrollToTop";

type OrderRequirements = Record<Requirement, boolean>;

const SingleProductPage = () => {
  const isMobile = useIsMobile();
  const { state } = useLocation();
  const { sku } = useParams<{ sku: string }>();

  const {
    shopInfo: routeShopInfo,
    participantID,
    customImage,
    isFromSamplePage = false,
  } = (state || {}) as {
    shopInfo: ImprovedParticipantShop;
    participantID: string;
    customImage: string;
    isFromSamplePage?: boolean;
  };

  const [productSKU, setProductSKU] = useState<string>(() => {
    const parsedSku = parseInt(sku || "", 10);
    return isNaN(parsedSku) ? "0" : parsedSku.toString();
  });
  const [style, setStyle] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    undefined,
  );
  const [customText, setCustomText] = useState<string | undefined>(undefined);
  const [clothingSize, setClothingSize] = useState<string | undefined>(
    undefined,
  );
  const [clothingCustomization, setClothingCustomization] = useState<
    string | undefined
  >(undefined);
  const [quantity, setQuantity] = useState<number>(1);
  const [price, setPrice] = useState<number>(0);
  const [fontStyle, setFontStyle] = useState<string | undefined>(undefined);
  const [cartItem, setCartItem] = useState<CartItem>(
    createEmptyCartItemNoCustom(),
  );
  const [isValidRoute, setIsValidRoute] = useState<boolean>(false);
  const [product, setProduct] = useState<ImprovedProduct | undefined>(
    undefined,
  );
  const [category, setCategory] = useState<Category>(Category.Default);
  const [orderRequirements, setOrderRequirements] = useState<OrderRequirements>(
    {} as OrderRequirements,
  );
  const [customTextFieldActive, setCustomTextFieldActive] =
    useState<boolean>(false);

  const getCategoryOfCurrentSKU = useCallback(
    (shopInfo: ImprovedParticipantShop, sku: number): Category => {
      for (const [cat, skus] of Object.entries(shopInfo.sku_by_category)) {
        if (
          (skus as number[]).includes(sku) &&
          Object.values(Category).includes(cat as Category)
        ) {
          return cat as Category;
        }
      }
      return Category.Default;
    },
    [],
  );

  const getProductBySKU = useCallback(
    (shopInfo: ImprovedParticipantShop): ImprovedProduct | undefined => {
      return shopInfo.products.find(
        (product) => product.sku === Number(productSKU),
      );
    },
    [productSKU],
  );

  useEffect(() => {
    const product = getProductBySKU(shopInfo);
    if (cartItem && getSKUCategory() !== Category.Default) {
      setCartItem((prevItem) => ({
        ...prevItem,
        sku: Number(productSKU),
        price: price,
        quantity: quantity,
        name: product?.name || "",
        image: product?.images[0] || "",
        customization: {
          ...prevItem.customization,
          bottle_text: customText,
          clothing_size: clothingSize,
          clothing_customization: clothingCustomization,
          font_style: fontStyle,
          design: customImage || "",
        },
      }));
    }
    if (cartItem && getSKUCategory() === Category.Default) {
      setCartItem((prevItem) => ({
        ...prevItem,
        sku: Number(productSKU),
        price: price,
        quantity: quantity,
        name: product?.name || "",
        image: product?.images[0] || "",
      }));
    }
  }, [
    price,
    customText,
    quantity,
    clothingSize,
    clothingCustomization,
    fontStyle,
    productSKU,
    style,
    selectedColor,
  ]);

  useEffect(() => {
    if (routeShopInfo && participantID && productSKU) {
      const currentProduct = routeShopInfo.products.find(
        (p) => p.sku === Number(productSKU),
      );
      const currentCategory = getCategoryOfCurrentSKU(
        routeShopInfo,
        Number(productSKU),
      );

      setProduct(currentProduct);
      setCategory(currentCategory);

      const requirements = getCategoryRequirements(currentCategory);
      const requirementsRecord = requirements.reduce<OrderRequirements>(
        (acc, req) => {
          acc[req] = true;
          return acc;
        },
        {} as OrderRequirements,
      );

      setOrderRequirements(requirementsRecord);
      setIsValidRoute(true);

      if (currentProduct) {
        setPrice(currentProduct.base_price);
      }
      if (isBottleCategory(currentCategory)) {
        setFontStyle(AVAILABLE_FONTS[0]);
      }
    } else {
      setIsValidRoute(false);
    }
  }, [routeShopInfo, participantID, productSKU, getCategoryOfCurrentSKU]);

  useEffect(() => {
    setStyle(getStyleNameByCategory(category) || "");
  }, [category]);

  const shopInfo = routeShopInfo;
  const participant = shopInfo?.participant || DEFAULT_PARTICIPANT;
  const defaultImage = "https://placehold.co/600x600?text=No+Image+Available";
  useEffect(() => {
    if (product) {
      setPrice(product.base_price || 0);
    }
  }, [product]);

  const getSKUCategory = (): Category => {
    for (const [category, skus] of Object.entries(shopInfo.sku_by_category)) {
      if (skus.includes(Number(productSKU))) {
        // Make sure the string is actually a valid Category value
        if (Object.values(Category).includes(category as Category)) {
          return category as Category;
        }
      }
    }
    return Category.Default;
  };

  useEffect(() => {
    const product = getProductBySKU(shopInfo);
    if (isClothingCategory(getSKUCategory())) {
      const clothingSizeUpcharge = shopInfo.clothing_sizes[productSKU].find(
        (size) => size.db_name === clothingSize,
      )?.up_charge;

      const clothingCustomizationUpcharge = CUSTOMIZATION_OPTIONS.find(
        (option) => option.text === clothingCustomization,
      )?.upcharge;

      if (
        clothingCustomizationUpcharge === undefined ||
        clothingCustomizationUpcharge === 0
      ) {
        setCustomTextFieldActive(false);
        setCustomText("");
      } else {
        setCustomTextFieldActive(true);
      }
      setPrice(
        (product?.base_price || 0) +
          (clothingSizeUpcharge || 0) +
          (clothingCustomizationUpcharge || 0),
      );
    } else {
      setPrice(product?.base_price || 0);
      setCustomTextFieldActive(true);
    }
  }, [
    clothingSize,
    clothingCustomization,
    productSKU,
    customTextFieldActive,
    getProductBySKU,
    shopInfo,
  ]);

  useEffect(() => {
    if (!product || !isClothingCategory(category) || !shopInfo) return;

    const clothingSizeUpcharge =
      (
        shopInfo.clothing_sizes as Record<
          string,
          Array<{ db_name: string; up_charge: number }>
        >
      )[productSKU]?.find(
        (size: { db_name: string; up_charge: number }) =>
          size.db_name === clothingSize,
      )?.up_charge || 0;

    const clothingCustomizationUpcharge =
      CUSTOMIZATION_OPTIONS.find(
        (option) => option.text === clothingCustomization,
      )?.upcharge || 0;

    setPrice(
      product.base_price + clothingSizeUpcharge + clothingCustomizationUpcharge,
    );
  }, [
    clothingSize,
    clothingCustomization,
    product,
    productSKU,
    category,
    shopInfo?.clothing_sizes,
  ]);

  if (!isValidRoute) {
    return <div>Invalid product information</div>;
  }

  if (!routeShopInfo) return <div>Invalid shop information</div>;

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="single-product-page-container">
      <ScrollToTop />
      <ProductImage
        imageUrl={product.images[0]}
        customImageUrl={category !== Category.Default ? customImage : undefined}
        category={category}
        isMobile={isMobile}
        fontStyle={fontStyle}
        customText={customText}
      />

      <div className="single-product-page-options">
        <ProductOptions
          orderRequirements={Object.entries(orderRequirements)
            .filter(([_, value]) => value)
            .map(([key]) => key as Requirement)}
          category={category}
          clothingSizes={shopInfo.clothing_sizes}
          productSKU={productSKU.toString()}
          clothingSize={clothingSize}
          setClothingSize={setClothingSize}
          clothingCustomization={clothingCustomization}
          setClothingCustomization={setClothingCustomization}
          customTextFieldActive={customTextFieldActive}
          fontStyle={fontStyle}
          setFontStyle={setFontStyle}
          customText={customText}
          setCustomText={setCustomText}
          style={style}
          setStyle={setStyle}
          availableFonts={AVAILABLE_FONTS}
          customizationOptions={CUSTOMIZATION_OPTIONS}
          setProductSKU={setProductSKU}
          setSelectedColor={setSelectedColor}
          selectedColor=""
          shopInfo={shopInfo}
        />

        {(
          Object.keys(orderRequirements) as Array<
            keyof typeof orderRequirements
          >
        ).includes(Requirement.Quantity as any) && (
          <ProductPrice
            price={price}
            quantity={quantity}
            onQuantityChange={setQuantity}
          />
        )}

        <ProductActions
          cartItem={cartItem}
          participant={participant}
          participantID={participantID}
          isFromSamplePage={isFromSamplePage}
        />
      </div>
    </div>
  );
};

export default SingleProductPage;
